// src/redux/slices/giftCardsSlice.js
import { createSlice } from '@reduxjs/toolkit';

// Dummy product data (you can replace these with real API data later)
const dummyProducts = [
  {
    id: 1,
    title: 'Cool Hoodie',
    type: 'hoodie',
    sizes: ['M', 'L', 'XL'],
    colors: ['blue', 'red'],
    fabric: 'Cotton',
    printMethod: 'Screen Printing',
    printPlacement: 'Front',
    price: 50,
    rating: 4,
    description: 'A cool hoodie with a comfortable fit and modern design.',
  },
  {
    id: 2,
    title: 'Sporty Sweatshirt',
    type: 'sweatshirt',
    sizes: ['S', 'M', 'L'],
    colors: ['black', 'green'],
    fabric: 'Polyester',
    printMethod: 'DTG',
    printPlacement: 'Back',
    price: 40,
    rating: 3,
    description: 'A sporty sweatshirt perfect for workouts.',
  },
  {
    id: 3,
    title: 'Classic Hoodie',
    type: 'hoodie',
    sizes: ['XS', 'S', 'M', 'L'],
    colors: ['red', 'yellow'],
    fabric: 'Cotton-Poly Blend',
    printMethod: 'Embroidery',
    printPlacement: 'Sleeve',
    price: 60,
    rating: 5,
    description: 'A classic hoodie with a vintage vibe.',
  },
  {
    id: 4,
    title: 'Urban Sweatshirt',
    type: 'sweatshirt',
    sizes: ['M', 'L', 'XL', '2XL'],
    colors: ['purple', 'black'],
    fabric: 'Cotton',
    printMethod: 'Heat Transfer',
    printPlacement: 'Hood',
    price: 55,
    rating: 4,
    description: 'An urban sweatshirt with a trendy design.',
  },
];

// Default filter criteria
const initialFilters = {
  personalize: false,
  productTypes: { hoodies: false, sweatshirts: false },
  sizes: { XS: false, S: false, M: false, L: false, XL: false, '2XL': false, '3XL': false },
  colors: { black: false, green: false, yellow: false, red: false, purple: false, custom: false },
  fabric: { cotton: false, polyester: false, cottonPolyBlend: false },
  printMethod: { screenPrinting: false, dtg: false, dtf: false, embroidery: false, heatTransfer: false },
  printPlacement: { front: false, back: false, sleeve: false, hood: false },
  customization: { oneColor: false, fullColor: false, logoOnly: false },
  orientation: '',
  corners: '',
  designStyle: { inter: false, prata: false, protestRevolution: false, promo: false },
  priceRange: 100,
  rates: { five: false, four: false, three: false },
};

const initialState = {
  products: dummyProducts,
  filters: initialFilters,
  filteredProducts: dummyProducts,
};

/**
 * Helper function to filter products based on current filter criteria.
 * For this demo, filtering is applied on:
 * - Product type (mapping 'hoodie' → 'hoodies'; otherwise 'sweatshirts')
 * - Sizes (if any are selected)
 * - Price (products above the selected priceRange are filtered out)
 * - Rating (if any rating is selected)
 */
function filterProducts(products, filters) {
  const selectedTypes = Object.entries(filters.productTypes)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const selectedSizes = Object.entries(filters.sizes)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const selectedRates = Object.entries(filters.rates)
    .filter(([_, value]) => value)
    .map(([key]) => {
      if (key === 'five') return 5;
      if (key === 'four') return 4;
      if (key === 'three') return 3;
      return null;
    })
    .filter((rate) => rate !== null);

  return products.filter((product) => {
    // Filter by product type if any selected
    if (selectedTypes.length > 0) {
      const prodTypeKey = product.type === 'hoodie' ? 'hoodies' : 'sweatshirts';
      if (!selectedTypes.includes(prodTypeKey)) return false;
    }
    // Filter by sizes if any selected
    if (selectedSizes.length > 0) {
      if (!product.sizes.some((size) => selectedSizes.includes(size))) return false;
    }
    // Filter by price range
    if (product.price > filters.priceRange) return false;
    // Filter by rating if any selected
    if (selectedRates.length > 0) {
      if (!selectedRates.includes(product.rating)) return false;
    }
    return true;
  });
}

const giftCardsSlice = createSlice({
  name: 'giftCards',
  initialState,
  reducers: {
    // Update a simple filter (e.g., personalize)
    setSimpleFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Update a nested filter (e.g., productTypes, sizes, colors, etc.)
    setNestedFilter: (state, action) => {
      const { category, key, value } = action.payload;
      state.filters[category][key] = value;
    },
    // Update a radio button filter (e.g., orientation or corners)
    setRadioFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Update the price range
    setPriceRange: (state, action) => {
      state.filters.priceRange = action.payload;
    },
    // Recompute filteredProducts based on current filters
    applyFilters: (state) => {
      state.filteredProducts = filterProducts(state.products, state.filters);
    },
    // Reset filters to default values
    resetFilters: (state) => {
      state.filters = { ...initialFilters };
      state.filteredProducts = state.products;
    },
  },
});

export const {
  setSimpleFilter,
  setNestedFilter,
  setRadioFilter,
  setPriceRange,
  applyFilters,
  resetFilters,
} = giftCardsSlice.actions;

export default giftCardsSlice.reducer;
